import { GLOBE_VIEW_CONFIG } from "./data/globe-view-config.ts";

export const COUNTRY_BOUNDARY_SOURCE_URL = GLOBE_VIEW_CONFIG.data.boundaryApiPath;

export type BoundaryCoordinate = [lon: number, lat: number];

export type BoundaryFeatureCollection = {
  type: "FeatureCollection";
  features: BoundaryFeature[];
};

type BoundaryFeature = {
  type: "Feature";
  geometry: BoundaryGeometry | null;
};

type BoundaryGeometry =
  | { type: "LineString"; coordinates: BoundaryCoordinate[] }
  | { type: "MultiLineString"; coordinates: BoundaryCoordinate[][] }
  | { type: "Polygon"; coordinates: BoundaryCoordinate[][] }
  | { type: "MultiPolygon"; coordinates: BoundaryCoordinate[][][] };

export function extractBoundaryLineStrings(collection: BoundaryFeatureCollection) {
  return collection.features.flatMap((feature) => extractGeometryLineStrings(feature.geometry));
}

export function splitAntimeridianLine(line: BoundaryCoordinate[], maxLongitudeDelta = 180) {
  if (line.length < 2) return [];
  const first = line[0];
  if (!first) return [];

  const segments: BoundaryCoordinate[][] = [];
  let current: BoundaryCoordinate[] = [first];

  for (let index = 1; index < line.length; index += 1) {
    const previous = line[index - 1];
    const next = line[index];
    if (!previous || !next) continue;
    const crossesAntimeridian = Math.abs(next[0] - previous[0]) > maxLongitudeDelta;

    if (crossesAntimeridian) {
      if (current.length > 1) segments.push(current);
      current = [next];
    } else {
      current.push(next);
    }
  }

  if (current.length > 1) segments.push(current);
  return segments;
}

function extractGeometryLineStrings(geometry: BoundaryGeometry | null): BoundaryCoordinate[][] {
  if (!geometry) return [];

  if (geometry.type === "LineString") return [cleanLine(geometry.coordinates)];
  if (geometry.type === "MultiLineString") return geometry.coordinates.map(cleanLine).filter(hasEnoughCoordinates);
  if (geometry.type === "Polygon") return geometry.coordinates.map(cleanLine).filter(hasEnoughCoordinates);
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) => polygon.map(cleanLine)).filter(hasEnoughCoordinates);
  }

  return [];
}

function cleanLine(line: BoundaryCoordinate[]) {
  return line.filter(isBoundaryCoordinate);
}

function hasEnoughCoordinates(line: BoundaryCoordinate[]) {
  return line.length > 1;
}

function isBoundaryCoordinate(value: unknown): value is BoundaryCoordinate {
  if (!Array.isArray(value)) return false;
  const [lon, lat] = value;
  return Number.isFinite(lon) && Number.isFinite(lat) && lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}
